import { Injectable, inject } from '@angular/core';
import { isEmptyObject } from '@wms/app/core';
import { ProjectService } from '@wms/app/services';
import { PrefabManager } from '@wms/app/workspace/page-workspace';
import { SecurityService, getObjectInMap } from '@wms/settings/security';
import { cloneDeep, findIndex, forEach, includes, isEqual, last } from 'lodash-es';
import { EventManagerService } from '@wms/app/event-manager';

@Injectable({
  providedIn: 'root'
})
export class AuthorizationService {
  hideMessage: any = true;
  interceptUrls: any
  securityService: SecurityService;
  prefabManager: PrefabManager;
  projectService: ProjectService;
  securityProps: any;
  security: any;
  serviceInterceptUrls: any = [];
  permissionsChangeArray: any = [];
  message: any;
  defaultPermissions = ['Everyone', 'Authenticated'];
  PERMISSION_PERMIT_ALL = 'PermitAll';
  PERMISSION_AUTHENTICATED = 'Authenticated';
  supportedOperations = ['get', 'put', 'post', 'delete', 'patch'];
  BASE_PATH_KEY = 'x-WM-BASE_PATH';
  RELATIVE_PATH_KEY = 'x-WM-RELATIVE_PATH';
  showNoPrefabsMessage: boolean;
  isPrefabActive: boolean;

  constructor(private eventManager: EventManagerService) {
    this.securityService = inject(SecurityService);
    this.prefabManager = inject(PrefabManager);
    this.projectService = inject(ProjectService);
    this.security = this.securityService.security;
    this.securityProps = this.securityService.securityProps;
  }


  //to get the intercept roles for the given attribute, to show in UI
  getInterceptPermissions(permission: any, roles?: any) {
    //for permission permit-all & authenticated, roles are always empty
    if (permission === this.PERMISSION_PERMIT_ALL) {
      return [this.defaultPermissions[0]];
    }
    if (permission === this.PERMISSION_AUTHENTICATED) {
      return [this.defaultPermissions[1]];
    }
    return roles;
  }

  //Returns the permission object of the longest matching urls
  getUrlPermissions(interceptUrlsMap: any, urls: any, method?: any) {
    let permission, urlObject;
    if (isEmptyObject(interceptUrlsMap)) {
      return { 'permission': 'Authenticated' };
    }
    forEach(urls, (url): any => {
      urlObject = interceptUrlsMap[url];
      if (urlObject && ((method && urlObject[method] && urlObject[method].permissions) || urlObject.permission || urlObject.permissions)) {
        permission = interceptUrlsMap[url];
        return false;
      }
    });
    if (permission) {
      return permission;
    }
    return { 'permission': 'Authenticated' };
  }

  //get the list of al project pages, to show them in permissions ui
  getPagesListForPermissions() {
    this.projectService.getPagesList().then(() => {
      let pages = this.projectService.getDetails().pages,
        pageServices: any = [],
        url;

      //sanity check for pages array
      if (pages) {
        //create service object for the page & push to the array
        pages.forEach((pageInfo: any) => {
          url = "/pages/" + pageInfo.name + "/**";

          var isPageType = pageInfo.type === 'PAGE';

          pageServices.push({
            id: pageServices.length,
            name: pageInfo.name,
            url: url,
            type: isPageType ? 'Page' : 'Partial',
            icon: isPageType ? 'wms-page' : 'wms-partial'
          });
        });
      }
      //return the page-services array
      this.security.pages = pageServices;
      this.interceptUrls = null
      this.buildPermissionsUI(true, 'Pages');
    });
  }

  getPrefabsListForPermissions() {
    this.showNoPrefabsMessage = false;

    let prefabServices: any = [],
      name,
      url;

    this.prefabManager.listAppPrefabs()
      .then(() => {
        if (this.prefabManager.appPrefabNamePropertiesMap) {
          forEach(this.prefabManager.appPrefabNamePropertiesMap, ((prefab: any) => {
            name = prefab.name;
            url = '/prefabs/' + name + '/**';
            let index: any;
            if (this.security.prefabs?.length) {
              index = findIndex(this.security.prefabs, {'name': name});
            }
            let prefabObj: any = {
              'id': prefabServices.length,
              'name': name,
              'url': url,
              'type': 'Prefab'
            };
            if (index > -1) {
              prefabObj['permission'] = this.security.prefabs[index].permission;
              prefabObj['roles'] = this.security.prefabs[index].roles;
            }
            prefabServices.push(prefabObj);
          }));

          //push the tmpArr into the scope
          this.security.prefabs = prefabServices;
          if (!prefabServices.length) {
            this.showNoPrefabsMessage = true;
          }
        } else {
          this.showNoPrefabsMessage = true;
        }
        this.interceptUrls = null
        this.buildPermissionsUI(true,'Prefab');
      });
  }


  //Returns the possible urls from longest to shortest
  getMatchingUrls(url: any) {
    let keys = url.split('/'),
      urls = [],
      lastKey;
    urls.push(url);
    lastKey = last(keys);
    if (lastKey === '**') {
      keys.pop();
    }
    while (keys.length) {
      urls.push(keys.join('/') + '/**');
      keys.pop();
    }
    return urls;
  }

  buildPermissionsUI(setPermissions?:boolean,type? : any) {
    this.hideMessage = true;

    let parentPermissions: any = {},
      interceptUrlsMap: any = {},
      matchingUrls,
      pageObject,
      prefabObject,
      isLoginPage: any;
    if (this.interceptUrls) {
        return;
    }
    (this.securityService.getSecurityInterceptURLs() as Promise<any>).then((response: any) => {
      if (isEqual(this.securityService._interceptUrls, response) && !this.prefabManager.reloadPrefabs && !setPermissions) {
        return;
      }
      if (this.isPrefabActive) {
        this.isPrefabActive = false;
        this.prefabManager.reloadPrefabs = false;
      }
      this.securityService._interceptUrls = response;
      this.interceptUrls = response;
      //for each intercept url, get the corresponding permission & assign to the scope model
      this.interceptUrls.forEach((interceptObj: any) => {
        interceptUrlsMap[interceptObj.urlPattern] = interceptObj;
        //Fall back when no permissions are set to the service
        if (interceptObj.urlPattern === '/**') {
          this.securityProps.servicesUrlMap[interceptObj.urlPattern] = interceptObj;
        }
        //for pages
        pageObject = getObjectInMap(interceptObj.urlPattern, this.security.pages);
        if (interceptObj.urlPattern && pageObject) {
          pageObject.permission = this.getInterceptPermissions(interceptObj.permission, interceptObj.roles);
          pageObject.roles = this.getInterceptPermissions(interceptObj.roles);
          pageObject.userChanged = true;
        } else {
          //for prefabs
          prefabObject = getObjectInMap(interceptObj.urlPattern, this.security.prefabs);
          if (interceptObj.urlPattern && prefabObject) {
            prefabObject.permission = this.getInterceptPermissions(interceptObj.permission, interceptObj.roles);
            prefabObject.roles = this.getInterceptPermissions(interceptObj.roles);
            prefabObject.userChanged = true;
          } else {
            //for services
            let currentServiceObj;
            this.securityProps.servicesUrlMap[interceptObj.urlPattern] = this.securityProps.servicesUrlMap[interceptObj.urlPattern] || {};
            //get the intercept permissions for the respective service based on the url
            if (interceptObj.httpMethod) {
              //maintaining an array of intercept urls obtained for services
              this.serviceInterceptUrls.push(interceptObj.urlPattern + interceptObj.httpMethod);
              this.securityProps.servicesUrlMap[interceptObj.urlPattern][interceptObj.httpMethod] = { 'permissions': {} };
              //if the service operation has method type, then retrieve the permissions for the specific service operation
              currentServiceObj = this.securityProps.servicesUrlMap[interceptObj.urlPattern][interceptObj.httpMethod].permissions;
            } else {
              //maintaining an array of intercept urls obtained for service operations
              this.serviceInterceptUrls.push(interceptObj.urlPattern);
              this.securityProps.servicesUrlMap[interceptObj.urlPattern] = { 'permissions': {} };
              //if the service does not have method type
              currentServiceObj = this.securityProps.servicesUrlMap[interceptObj.urlPattern].permissions;
            }

            //get the intercept permissions for the specific url pattern and method type
            currentServiceObj.accessLevel = [];
            if (!isEmptyObject(interceptUrlsMap)) {
              matchingUrls = this.getMatchingUrls(interceptObj.urlPattern);
              parentPermissions = this.getUrlPermissions(interceptUrlsMap, matchingUrls);
              currentServiceObj.accessLevel = this.getInterceptPermissions(parentPermissions.permission, parentPermissions.roles);
            }
          }
        }
      });
      //For pages which no permissions set getting them from its parent
      forEach(this.security.pages, (page) => {
        if (includes(['Login', 'Common'], page.name)) {
          page.disabled = true;
          isLoginPage = true;
        }
        if (!page.permission) {
          parentPermissions = this.getUrlPermissions(interceptUrlsMap, [page.url, '/pages/**', '/**']);
          //Default Common and Login pages will be 'permitAll'
          if (isLoginPage) {
            page.permission = this.getInterceptPermissions(this.PERMISSION_PERMIT_ALL);
          } else {
            page.permission = page.roles = this.getInterceptPermissions(parentPermissions.permission, parentPermissions.roles);
          }
        }
        isLoginPage = false;
      });
      //For prefabs which no permissions set getting them from its parent
      forEach(this.security.prefabs, (prefab) => {
        if (!prefab.permission) {
          parentPermissions = this.getUrlPermissions(interceptUrlsMap, [prefab.url, '/prefabs/**', '/**']);
          prefab.permission = prefab.roles = this.getInterceptPermissions(parentPermissions.permission, parentPermissions.roles);
        }
      });
      if(type)
      this.eventManager.emitpagePermission();
    }, (errMsg) => {
      this.hideMessage = false;
      this.message = {
        type: 'error',
        caption: errMsg
      };
    });
  }
}
